// @ts-nocheck
// yjs typing library contains wrong typings that cause errors
import * as Y from "yjs"
import { Subject } from "rxjs"
import * as decoding from "lib0/decoding"
import { LazyStructReader, readDeleteSet } from "@features/builder/crdt/crdt"
import { ContentString } from "yjs"

type StructItem = Y.Item | Y.GC | Y.Skip

type StackAction = "add" | "update" | "remove"

type StackActionKey = "id" | "version" | "data" | "nodes" | "groups"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface StackMessage {
  key: StackActionKey
  action: StackAction
  value: string
  next?: string
}

interface IStructDataStructure {
  equal(id?: Y.ID | null): boolean
  add(item: Y.Item): StackMessage | boolean
}

/**
 * Value hold in StructMap->mapValues map
 */
class MapValue {
  constructor(readonly key: StackActionKey, readonly item: Y.Item) {}

  get value(): string {
    if ("str" in this.item.content) return this.item.content.str
    // other types not supported right now - this.item.content
    return ""
  }

  equals(client: number, clock: number): boolean {
    return this.item.id.client === client && this.item.id.clock === clock
  }

  equalsParent(client: number, clock: number): boolean {
    if (!this.item.parent || !("client" in this.item.parent)) return false
    return this.item.parent.client === client && this.item.parent.clock === clock
  }
}

/**
 * Store map with string / other type value inside ex id, data, version, graph
 */
class StructData implements IStructDataStructure {
  constructor(
    private key: StackActionKey,
    readonly item: Y.Item,
    private stack: Y.Item[],
    private subject: Subject<StackMessage>,
  ) {}

  add(item: Y.Item): StackMessage {
    this.stack.push(item)
    if (this.stack.length === 0) {
      const msg: StackMessage = {
        key: this.key,
        action: "add",
        value: (item.content as ContentString).str,
      }
      this.subject.next(msg)
      return msg
    }
    const value = this.stack[this.stack.length - 1]
    const msg: StackMessage = {
      key: this.key,
      action: "update",
      value: (value.content as ContentString).str,
      next: (item.content as ContentString).str,
    }
    this.subject.next(msg)
    return msg
  }

  get content() {
    const c = this.stack[this.stack.length - 1]?.content
    if (c && "str" in c) return c.str
    return c
  }

  equal(id?: Y.ID | null): boolean {
    if (!id) return false
    return id.client == this.item.id.client && id.clock == this.item.id.clock
  }
}

/**
 * Store map with map values inside ex nodes and groups
 */
class StructMap implements IStructDataStructure {
  private mapValues = new Map<string, MapValue>()
  private mapValuesClock = new Map<string, string>()
  constructor(
    private key: StackActionKey,
    readonly item: Y.Item,
    private stack: Map<string, Y.Item>,
    private subject: Subject<StackMessage>,
  ) {}

  add(item: Y.Item): boolean {
    this.stack.set(`${item.id.client}_${item.id.clock}`, item)
    return true
  }

  removeValue(client: number, clock: number): StackMessage | false {
    const key = `${client}_${clock}`
    // check for key in mapValues
    const mapKey = this.mapValuesClock.get(key)
    const stackItem = this.stack.get(key)
    // the value to remove is handle of our item so we need to go to stack to remove it
    if (stackItem && !mapKey) return this.removeStack(client, clock, stackItem)
    else if (!mapKey) return false
    // TODO probably dead code because we have hardcoded data structure
    this.mapValuesClock.delete(key)
    // if delete item coords equal map item coords remove from map
    const mapValue = this.mapValues.get(mapKey)
    if (!mapValue || !mapValue?.equals(client, clock)) return
    this.mapValues.delete(mapKey)
    const msg: StackMessage = { key: this.key, action: "remove", value: mapValue.value }
    this.subject.next(msg)
    return msg
  }

  private removeStack(client: number, clock: number, item: Y.Item): StackMessage | false {
    // calculate child key and get mapKey once again
    const key = `${client}_${clock + item.length}`
    const mapKey = this.mapValuesClock.get(key)
    if (!mapKey) return false
    this.mapValuesClock.delete(key)
    // check for value parent equals removed value and if so remove it
    const mapValue = this.mapValues.get(mapKey)
    if (!mapValue || !mapValue?.equalsParent(client, clock)) return false
    this.mapValues.delete(mapKey)
    const valueKey = `${mapValue?.item.id.client}_${mapValue?.item.id.clock}`
    // TODO check if we can remove this value from stack - it should have no parent or origin
    this.stack.delete(valueKey)
    const msg = { key: this.key, action: "remove", value: mapValue.value }
    this.subject.next(msg)
    return msg
  }

  replace(item: Y.Item): boolean {
    if (!item.origin) return false
    // const id = item.origin
    // this.stack.delete(`${id.client}_${id.clock}`)
    this.stack.set(`${item.id.client}_${item.id.clock}`, item)
    return true
  }

  addValue(item: Y.Item, id?: Y.ID): StackMessage | boolean {
    if (!id) return false
    const key = this.getKey(id)
    const nextValue = new MapValue(key, item)
    if (this.mapValues.has(key)) {
      const msg = {
        key: this.key,
        action: "update",
        value: this.mapValues.get(key)?.value,
        next: nextValue.value,
      }
      this.subject.next(msg)
      this.mapValues.set(key, nextValue)
      this.mapValuesClock.set(`${item.id.client}_${item.id.clock}`, key)
      return msg
    }
    const msg = { key: this.key, action: "add", value: nextValue.value }
    this.subject.next(msg)
    this.mapValues.set(key, nextValue)
    this.mapValuesClock.set(`${item.id.client}_${item.id.clock}`, key)
    return msg
  }

  getValue(key: string) {
    return this.mapValues.get(key)
  }

  hasValue(id?: Y.ID | null): boolean {
    if (!id) return false
    return this.mapValuesClock.has(`${id.client}_${id.clock}`)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  has(id?: Y.ID | null): boolean {
    if (!id) return false
    return this.stack.has(`${id.client}_${id.clock}`)
  }

  equal(id?: Y.ID | null): boolean {
    if (!id) return false
    return id.client == this.item.id.client && id.clock == this.item.id.clock
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getKey(id: Y.ID | Y.AbstractType<any>): StackActionKey {
    if (!("client" in id)) throw new Error("Unexpected type in getKey 'id' argument")
    const key = `${id.client}_${id.clock}`
    const item = this.stack.get(key)
    if (!item) throw new Error(`Item not found for key ${key} - probably broken file`)
    if (item.parentSub) return item.parentSub as StackActionKey
    if (item.parent) return this.getKey(item.parent)
    if (item.origin) return this.getKey(item.origin)
    throw new Error(`Problem getting key ${key} - check left / right values`)
  }
}

/**
 * Store graph document from binary messages back into stacked nodes
 */
export class StructStack {
  private clients = new Map<number, Map<number, Y.Item>>()
  private version?: StructData = undefined
  private id?: StructData = undefined
  private data?: StructData = undefined
  private nodes?: StructMap = undefined
  private groups?: StructMap = undefined
  readonly onChange = new Subject<StackMessage>()

  /**
   * Update stack with new messages and deleteSet
   */
  update({ structs, deletedSet }, logStructs = false): StackMessage[] {
    // process structs
    const out: StackMessage[] = []
    const pending = []
    for (const item of structs) {
      // Y.Item
      if ("origin" in item) {
        this.addStruct(item)
        if (logStructs) this.logStruct(item)
        const processed = this.processItem(item)
        if (!processed) pending.push(item)
        if (!(typeof processed == "boolean")) out.push(processed)
      } else if (logStructs) {
        // Y.GC, Y.Skip
        // console.log("ignored gc|skip", item)
      }
    }
    // process pending messages - when we delete things - messages are processed out of order
    const pendingNext = []
    while (pending.length > 0) {
      for (let i = 0; i < pending.length; i++) {
        if (this.processItem(pending[i])) pending.splice(i, 1)
        else pendingNext.push(pending[i])
      }
      // not a single one pending items was processed, so we escape while true
      if (pendingNext.length === pending.length && pending.length > 0) {
        // eslint-disable-next-line
        console.error("StructStack problem with processing pending items", pending, this)
        break
      }
      pendingNext.splice(0)
    }

    // process deletions
    for (const [clientID, arr] of deletedSet.clients.entries()) {
      for (const item of arr) {
        const nodeMsg = this.nodes?.removeValue(clientID, item.clock)
        if (nodeMsg && nodeMsg !== false) out.push(nodeMsg)
        const grpMsg = this.groups?.removeValue(clientID, item.clock)
        if (grpMsg && grpMsg !== false) out.push(grpMsg)
      }
    }
    // console.log(this, "version", this.version?.content, "id", this.id?.content, "data", this.data?.content, "nodes", this.nodes, "groups", this.groups)
    // console.log(this, "structs", structs, "deleteSet", deletedSet)
    return out
  }

  getNode(id: string) {
    return this.nodes.getValue(id)?.value
  }

  getGroup(id: string) {
    return this.groups.getValue(id)?.value
  }

  /**
   * Process single item
   */
  private processItem(item: Y.Item): StackMessage | boolean {
    if (item.parent === "version") {
      this.version = new StructData("version", item, [item], this.onChange)
      this.deleteStruct(item.id.client, item.id.clock)
      return true
    }
    if (item.parent === "graph") {
      switch (item.parentSub) {
        case "id": {
          this.id = new StructData("id", item, [], this.onChange)
          this.deleteStruct(item.id.client, item.id.clock)
          return true
        }
        case "data": {
          this.data = new StructData("data", item, [], this.onChange)
          this.deleteStruct(item.id.client, item.id.clock)
          return true
        }
        case "nodes": {
          this.nodes = new StructMap("nodes", item, new Map(), this.onChange)
          this.deleteStruct(item.id.client, item.id.clock)
          return true
        }
        case "groups": {
          this.groups = new StructMap("groups", item, new Map(), this.onChange)
          this.deleteStruct(item.id.client, item.id.clock)
          return true
        }
        default: {
          // eslint-disable-next-line no-console
          console.log("not implemented", item.parentSub, item)
          throw new Error(`Not implemented ${item.parentSub}`)
        }
      }
    }
    let msg = this.handleTopLevelChange(item)
    if (msg) return msg
    msg = this.handleAddMap(item, this.groups)
    if (msg) return msg
    return this.handleAddMap(item, this.nodes)
  }

  private handleTopLevelChange(item: Y.Item): StackMessage | boolean {
    let msg = this.handleOriginParentChange(item, this.version)
    if (msg) return msg
    msg = this.handleOriginParentChange(item, this.id)
    if (msg) return msg
    msg = this.handleOriginParentChange(item, this.data)
    if (msg) return msg
    msg = this.handleOriginParentChange(item, this.nodes)
    if (msg) return msg
    return this.handleOriginParentChange(item, this.groups)
  }

  private handleOriginParentChange(
    item: Y.Item,
    value?: IStructDataStructure,
  ): StackMessage | boolean {
    if (value?.equal(item.origin)) {
      // eslint-disable-next-line
      console.error(`Not implemented change origin id ${item.id.client} clock ${item.id.clock}`)
    } else if (item.parent && value?.equal(item.parent)) {
      const msg = value?.add(item)
      this.deleteStruct(item.id.client, item.id.clock)
      return msg
    }
  }

  private handleAddMap(item: Y.Item, structMap?: StructMap): StackMessage | boolean {
    if (!structMap) return false
    if (structMap.equal(item.parent)) {
      structMap.add(item)
      this.deleteStruct(item.id.client, item.id.clock)
      return true
    } else if (structMap.has(item.parent)) {
      const change = structMap.addValue(item, item.parent)
      this.deleteStruct(item.id.client, item.id.clock)
      return change
    } else if (structMap.hasValue(item.origin)) {
      // This probably would not happen if we keep replacing nodes instead of replacing values inside nodes
      // console.log("TODO replace.value", item)
      return true
    } else if (structMap.has(item.origin)) {
      structMap.replace(item)
      // console.log("TODO replace.origin", item)
      this.deleteStruct(item.id.client, item.id.clock)
      return true
    }
    return false
  }

  /**
   * Remove struct from parsing - done after message has been processed
   */
  private deleteStruct(client: number, clock: number): boolean {
    const items = this.clients.get(client)
    if (!items) {
      // console.log("delete problem", client, clock, this.clients.keys())
      return false
    }
    const deleted = items.delete(clock)
    if (items.size === 0) this.clients.delete(client)
    return deleted
  }

  /**
   * Remove struct to parse
   */
  private addStruct(item: Y.Item) {
    let structs = this.clients.get(item.id.client)
    if (!structs) {
      structs = new Map()
    }
    structs.set(item.id.clock, item)
    this.clients.set(item.id.client, structs)
  }

  private logStruct(item: Y.Item) {
    if ("str" in item.content && item.content?.str) {
      try {
        // eslint-disable-next-line no-console
        console.log(JSON.parse(item.content.str))
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(item.content?.str || item)
      }
    }
  }
}

export const readEvent = (e) => {
  const structs = []
  const updateDecoder = new Y.UpdateDecoderV1(decoding.createDecoder(e))
  const lazyDecoder = new LazyStructReader(updateDecoder, false)
  for (let curr = lazyDecoder.curr; curr !== null; curr = lazyDecoder.next()) {
    structs.push(curr)
  }
  const deletedSet = readDeleteSet(updateDecoder)
  return { structs, deletedSet }
}

export const uint8ToString = (u8a) => {
  const CHUNK_SZ = 0x8000
  const c = []
  for (let i = 0; i < u8a.length; i += CHUNK_SZ) {
    c.push(String.fromCharCode.apply(null, u8a.subarray(i, i + CHUNK_SZ)))
  }
  return c.join("")
}

export const toB64 = (u8a) => btoa(uint8ToString(u8a))
export const fromB64 = (val) =>
  new Uint8Array(
    atob(val)
      .split("")
      .map(function (c) {
        return c.charCodeAt(0)
      }),
  )
